import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { verifyEmail } from '../api/client'
import { FactoryLogo } from '../components/FactoryLogo'
import { LoadingSpinner } from '../components/LoadingSpinner'

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState('')
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setError('Missing verification token')
      return
    }

    verifyEmail(token)
      .then((result) => {
        setStatus('success')
        // Store new tokens with updated email_verified claim
        if (result.access_token) {
          localStorage.setItem('factory_access_token', result.access_token)
        }
        if (result.refresh_token) {
          localStorage.setItem('factory_refresh_token', result.refresh_token)
        }
        setTimeout(() => navigate('/dashboard', { replace: true }), 3000)
      })
      .catch((err) => {
        setStatus('error')
        setError(err instanceof Error ? err.message : 'Verification failed')
      })
  }, [token, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <FactoryLogo className="w-16 h-16" />
        </div>

        {status === 'loading' && (
          <>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Verifying your email...</h2>
            <LoadingSpinner size="lg" />
          </>
        )}

        {status === 'success' && (
          <>
            <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Email verified!</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Your email has been verified. Redirecting to dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Verification failed</h2>
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Link
              to="/login"
              className="inline-block mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Go to login
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
